import type { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { NotificationStore } from '@indutrust/shared-data';
import { resolveHttpError, withRequestStatus } from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import { signalStore, withComputed } from '@ngrx/signals';
import {
  Events,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { switchMap } from 'rxjs';
import { VrCaptureRequestApiClient } from '../vr-capture-request-api-client.service';
import { vrCaptureRequestEvents } from './vr-capture-request.events';

/**
 * Pattern B store for assisted VR capture booking on `/virtual-tour`.
 *
 * Tracks submit loading/error state and surfaces success/error toasts via
 * {@link NotificationStore} when the API call completes.
 */
export const VrCaptureRequestStore = signalStore(
  { providedIn: 'root' },
  withRequestStatus(),
  withComputed(({ isLoading, error }) => ({
    isSubmitting: isLoading,
    submitError: error,
  })),
  withReducer(
    on(vrCaptureRequestEvents.submitRequested, () => ({
      requestStatus: 'loading' as const,
    })),
    on(vrCaptureRequestEvents.submitSucceeded, () => ({
      requestStatus: 'success' as const,
    })),
    on(vrCaptureRequestEvents.submitFailed, ({ payload }) => ({
      requestStatus: { error: payload.error },
    }))
  ),
  withEventHandlers(
    (
      _,
      events = inject(Events),
      api = inject(VrCaptureRequestApiClient),
      notificationStore = inject(NotificationStore)
    ) => ({
      submit$: events.on(vrCaptureRequestEvents.submitRequested).pipe(
        switchMap(({ payload }) =>
          api.create(payload.factoryId, payload.payload).pipe(
            mapResponse({
              next: () => {
                notificationStore.notify({
                  config: {
                    messageKey: 'toast.successMessage',
                    scope: 'shared-factory-virtual-tour-page',
                    titleKey: 'toast.successTitle',
                    type: 'success',
                  },
                });
                return vrCaptureRequestEvents.submitSucceeded();
              },
              error: (error: HttpErrorResponse) => {
                notificationStore.notify({
                  config: {
                    messageKey: 'toast.errorMessage',
                    scope: 'shared-factory-virtual-tour-page',
                    titleKey: 'toast.errorTitle',
                    type: 'error',
                  },
                });
                return vrCaptureRequestEvents.submitFailed({
                  error: resolveHttpError(error).code,
                });
              },
            })
          )
        )
      ),
    })
  )
);
